// Typed wrapper around auth-service REST. Every response is validated by Zod
// at the boundary — anything past this module can rely on the shapes below.

import { z } from "zod";

import { config } from "@/lib/config";
import { networkError, parseErrorResponse, type ApiError } from "@/lib/api/errors";
import { tokenStore } from "@/lib/auth/tokenStore";

const ROLE = z.enum(["platform_admin", "admin", "ops_manager", "trader", "executive"]);
export type Role = z.infer<typeof ROLE>;

const tokensSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string().optional(), // omitted when the server uses HTTP-only cookies
  token_type: z.string().default("Bearer"),
  // Matches the backend TokenEnvelope field name (services/auth-service/app/schemas.py
  // emits `expires_in_seconds`). Optional and unused at the boundary — token expiry is
  // derived from the JWT `exp` claim via tokenStore.expiresInSeconds() — but the field
  // name is kept aligned with the contract so it parses the real value when present.
  expires_in_seconds: z.number().int().nonnegative().optional(),
});

const userSchema = z.object({
  user_id: z.number().int(),
  tenant_id: z.number().int(),
  tenant_slug: z.string(),
  email: z.string(),
  role: ROLE,
  mfa_setup_required: z.boolean().default(false),
});

const loginSuccessSchema = z.object({
  user_id: z.number().int(),
  tenant_id: z.number().int(),
  tenant_slug: z.string(),
  email: z.string(),
  role: ROLE,
  mfa_setup_required: z.boolean().default(false),
  tokens: tokensSchema,
});

const loginChallengeSchema = z.object({
  challenge_token: z.string(),
  mfa_required: z.literal(true),
  // First-login enrollment flag: when true the user has no TOTP secret yet and
  // must run the /auth/mfa/setup → /auth/mfa/verify enrollment flow before any
  // tokens are issued. Already-enrolled users omit it (defaults to false).
  mfa_setup_required: z.boolean().default(false),
});

const loginResponseSchema = z.union([loginSuccessSchema, loginChallengeSchema]);

const refreshResponseSchema = z.object({ tokens: tokensSchema });

// /auth/mfa/setup response (pre-login enrollment): the provisioning URI + a
// server-rendered QR SVG. `mfa_enabled` is false here — enrollment only
// completes once the code is verified.
const mfaSetupSchema = z.object({
  mfa_enabled: z.boolean(),
  provisioning_uri: z.string(),
  qr_svg: z.string().optional(),
});

// GET /auth/me — the authoritative current-user profile for account settings.
// This is the MFA-enrollment source of truth (the JWT does not carry it), which
// fixes the "Enable MFA shown while already enrolled" bug.
const meSchema = z.object({
  user_id: z.number().int(),
  tenant_id: z.number().int(),
  tenant_slug: z.string(),
  email: z.string(),
  role: ROLE,
  full_name: z.string().nullable().optional(),
  mfa_enabled: z.boolean().default(false),
  mfa_verified_at: z.string().nullable().optional(),
  email_verified: z.boolean().default(false),
  pending_email: z.string().nullable().optional(),
  last_login_at: z.string().nullable().optional(),
  notification_prefs: z.record(z.unknown()).default({}),
});

const okSchema = z.object({ status: z.string() });
const mfaStatusSchema = z.object({ mfa_enabled: z.boolean(), verified_at: z.string().nullable().optional() });

export type LoginSuccess = z.infer<typeof loginSuccessSchema>;
export type LoginChallenge = z.infer<typeof loginChallengeSchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;
export type MfaSetupResponse = z.infer<typeof mfaSetupSchema>;
export type AuthUser = z.infer<typeof userSchema>;
export type Me = z.infer<typeof meSchema>;

// refresh() resolves to this sentinel (instead of throwing) when the
// refresh cookie is missing/expired — a 401 with code "refresh_required".
// First-load visitors hit this every time; it is a normal logged-out state,
// not an error, so callers branch on it without try/catch noise.
export const LOGGED_OUT = { kind: "logged_out" } as const;
export type LoggedOut = typeof LOGGED_OUT;

export function isLoggedOut(result: RefreshResponse | LoggedOut): result is LoggedOut {
  return "kind" in result && result.kind === "logged_out";
}

// Pull a string `code` out of `{ [key]: { code } }`, e.g. `{detail:{code}}`.
function readNestedCode(body: unknown, key: "detail" | "error"): string | undefined {
  const nested = (body as Record<string, unknown> | null | undefined)?.[key];
  const code = (nested as Record<string, unknown> | null | undefined)?.code;
  return typeof code === "string" ? code : undefined;
}

// Pull a top-level string `code` out of `{ code }`.
function readTopCode(body: unknown): string | undefined {
  const code = (body as Record<string, unknown> | null | undefined)?.code;
  return typeof code === "string" ? code : undefined;
}

type Json = Record<string, unknown> | undefined;

async function sendJson<S extends z.ZodTypeAny>(
  method: "GET" | "POST" | "PATCH",
  path: string,
  body: Json,
  schema: S,
  withAuth = false,
): Promise<z.output<S>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (withAuth && tokenStore.accessToken) {
    headers.Authorization = `Bearer ${tokenStore.accessToken}`;
  }
  let response: Response;
  try {
    response = await fetch(`${config.authBaseUrl}${path}`, {
      method,
      headers,
      // `credentials: include` is required for the HTTP-only refresh cookie
      // round trip; for in-memory-only mode it's a harmless no-op.
      credentials: "include",
      body: method === "GET" || !body ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    throw networkError(error);
  }
  if (!response.ok) {
    const apiError: ApiError = await parseErrorResponse(response);
    throw apiError;
  }
  const json = (await response.json().catch(() => ({}))) as unknown;
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw {
      code: "unknown",
      message: "auth-service returned an unexpected response shape",
      status: response.status,
      detail: parsed.error.issues,
    } satisfies ApiError;
  }
  return parsed.data;
}

// Back-compat thin wrapper — most callers POST.
function postJson<S extends z.ZodTypeAny>(path: string, body: Json, schema: S, withAuth = false) {
  return sendJson("POST", path, body, schema, withAuth);
}

const realAuthClient = {
  login(email: string, password: string): Promise<LoginResponse> {
    return postJson("/auth/login", { email, password }, loginResponseSchema);
  },

  // --- Account self-service (authenticated) -------------------------------
  getMe(): Promise<Me> {
    return sendJson("GET", "/auth/me", undefined, meSchema, true);
  },
  updateProfile(input: { full_name?: string | null; notification_prefs?: Record<string, unknown> }): Promise<Me> {
    return sendJson("PATCH", "/auth/me", input, meSchema, true);
  },
  changePassword(currentPassword: string, newPassword: string): Promise<{ status: string }> {
    return postJson("/auth/change-password", { current_password: currentPassword, new_password: newPassword }, okSchema, true);
  },
  // Enroll MFA from settings (authenticated, no challenge token): setup → verify.
  enrollMfaStart(): Promise<MfaSetupResponse> {
    return postJson("/auth/mfa/setup", {}, mfaSetupSchema, true);
  },
  enrollMfaVerify(code: string): Promise<{ mfa_enabled: boolean }> {
    return postJson("/auth/mfa/verify", { code }, mfaStatusSchema, true);
  },
  disableMfa(password: string, code: string): Promise<{ status: string }> {
    return postJson("/auth/mfa/disable", { password, code }, okSchema, true);
  },
  requestEmailChange(newEmail: string, password: string): Promise<{ status: string }> {
    return postJson("/auth/email/request-change", { new_email: newEmail, password }, okSchema, true);
  },
  resendVerification(): Promise<{ status: string }> {
    return postJson("/auth/email/resend-verification", {}, okSchema, true);
  },

  // --- Recovery / verification (logged-out, token-authenticated) ----------
  forgotPassword(email: string): Promise<{ status: string }> {
    return postJson("/auth/forgot-password", { email }, okSchema, false);
  },
  resetPassword(token: string, newPassword: string): Promise<{ status: string }> {
    return postJson("/auth/reset-password", { token, new_password: newPassword }, okSchema, false);
  },
  verifyEmail(token: string): Promise<{ status: string }> {
    return postJson("/auth/email/verify", { token }, okSchema, false);
  },

  verifyMfa(challengeToken: string, code: string): Promise<LoginSuccess> {
    // verify endpoint can return either success (with tokens) or mfa_required again
    return postJson("/auth/mfa/verify", { challenge_token: challengeToken, code }, loginSuccessSchema);
  },

  // First-login enrollment (pre-login): exchange the challenge token for a
  // provisioning URI + QR. Tokens are NOT issued here — enrollment completes
  // once the resulting TOTP code is checked via verifyMfa().
  setupMfa(challengeToken: string): Promise<MfaSetupResponse> {
    return postJson("/auth/mfa/setup", { challenge_token: challengeToken }, mfaSetupSchema);
  },

  async refresh(): Promise<RefreshResponse | LoggedOut> {
    // The refresh token rides in the HTTP-only cookie. A 401 with code
    // "refresh_required" is the expected logged-out state (no/expired cookie,
    // e.g. first-time visitors) — we resolve to the LOGGED_OUT sentinel rather
    // than throwing so it produces no console/network error noise. Any other
    // failure is a real error and still throws.
    let response: Response;
    try {
      response = await fetch(`${config.authBaseUrl}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch (error) {
      throw networkError(error);
    }
    // A 401 on /auth/refresh is, by definition, a missing/expired session —
    // auth-service signals it with code "refresh_required". We never surface it
    // as an error (first-load visitors hit it every time); we resolve to the
    // LOGGED_OUT sentinel so callers branch on a value rather than catch a throw.
    if (response.status === 401) {
      const apiError = await parseErrorResponse(response);
      const detail = apiError.detail;
      // The backend code lives in one of: {detail:{code}}, {error:{code}},
      // or a top-level {code}. We read it only for the log line below — any
      // 401 here is treated as logged-out regardless.
      const backendCode =
        readNestedCode(detail, "detail") ?? readNestedCode(detail, "error") ?? readTopCode(detail);
      if (backendCode && backendCode !== "refresh_required") {
        // Unexpected 401 code (e.g. a malformed token) — still logged out, but
        // worth a debug breadcrumb. Intentionally not thrown.
        console.debug("auth_refresh_401", backendCode);
      }
      return LOGGED_OUT;
    }
    if (!response.ok) {
      throw await parseErrorResponse(response);
    }
    const json = (await response.json().catch(() => ({}))) as unknown;
    const parsed = refreshResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw {
        code: "unknown",
        message: "auth-service returned an unexpected response shape",
        status: response.status,
        detail: parsed.error.issues,
      } satisfies ApiError;
    }
    return parsed.data;
  },

  async logout(): Promise<void> {
    let response: Response;
    try {
      response = await fetch(`${config.authBaseUrl}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: tokenStore.accessToken
          ? { Authorization: `Bearer ${tokenStore.accessToken}`, "Content-Type": "application/json" }
          : { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    } catch (error) {
      throw networkError(error);
    }
    if (!response.ok) {
      // Surface 503 explicitly so the UI can prompt a retry (auth-service
      // returns 503 when the revocation store is down — see Batch D1).
      throw await parseErrorResponse(response);
    }
  },

  acceptInvitation(token: string, password: string): Promise<LoginSuccess> {
    return postJson("/auth/invitations/accept", { token, password }, loginSuccessSchema);
  },
};

export const authClient = realAuthClient;

export { userSchema };
